import { memo, useState } from 'react';

import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { Mediator } from '@spotahome/soyuz-mediator';

import {
  BestChoiceLabelCircledIcon,
  InstantBookingLabelCircledIcon,
  BillsLabelCircledIcon,
  PlusLabelCircledIcon,
  LongTermLabelCircledIcon,
  LockRoundedIcon,
  Button,
  CHANNELS
} from '@spotahome/ui-library';

import PiggyBankLabelCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/PiggyBankLabelCircledIcon';
import HouseLabelCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/HouseLabelCircledIcon';

import LongTermDescription from '@spotahome/ui-library/src/tenant/components/LongTermDescription';
import PlusLabelDescription from '@spotahome/ui-library/src/tenant/components/PlusLabelDescription';
import VerificationIconWrapper from '@spotahome/ui-library/src/tenant/components/VerificationIconWrapper';
import CheckedIconPhoto from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/ChekedIconPhoto';
import CheckedInfo from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/CheckedInfo';
import LLInfo from '@spotahome/ui-library/src/tenant/components/ListingLabel/LLInfo';
import TitleChecked from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/TitleChecked';
import CheckedDescription from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/CheckedDescription';
import VerificationModal from '@spotahome/ui-library/src/tenant/components/ListingLabel/VerificationModal/index';

import { HomeCheckerShape } from '@spotahome/ui-library/src/tenant/shapes/HomeChecker';

import instantBookingShape from '@spotahome/marketplace-common/src/shapes/instantBooking';

import { useLandlordInformation } from '../../context/LandlordInformationContext';

import ListingLabel from './ListingLabel';
import InstantBookingLabelDescription from './InstantBookingLabelDescription';

import './ListingLabels.scss';

const ListingLabels = memo(
  ({
    isChecked,
    hasNoDeposit,
    instantBooking,
    allBillsIncluded,
    someBillsIncluded,
    isBestChoice,
    isPlusPlan,
    allowsPermanentContract,
    homechecker,
    verificationDate,
    landlordType,
    landlordId
  }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { landlordInformation, loading } = useLandlordInformation(landlordId);

    const openVerificationModal = () => {
      SoyuzAnalytics.sendGA4Event('open', {
        section: 'more-about-verification'
      });
      setIsModalOpen(true);
    };

    const getTitle = title => {
      return <span>{title}</span>;
    };

    const handleOnClick = () => {
      SoyuzAnalytics.sendGA4Event('ll-info-preview-click-listing', {});
      const llSection = document.querySelector(
        '[data-sticky-in-navigation="aboutLandlord"]'
      );
      window.scrollTo({
        top: llSection.getBoundingClientRect().top - 70, // extra height for top navigation
        behavior: 'smooth'
      });
    };

    const labelsDict = [
      {
        id: 'depositProtected',
        renderIcon: () => <LockRoundedIcon />,
        renderTitle: () =>
          getTitle(trans('listing.labels.deposit-protected.title')),
        renderDescription: () => (
          <p className="listing-labels__deposit-body">
            {trans('listing.labels.deposit-protected.description')}{' '}
            <Button
              color="link"
              onClick={() => {
                SoyuzAnalytics.sendGA4Event('deposit-protection-more', {});
                Mediator.publish(CHANNELS.OPEN_DEPOSIT_PROTECTION_MODAL);
              }}
            >
              {trans('home-ll.cta')} {/* More information (reused)*/}
            </Button>
          </p>
        ),
        enabled: !hasNoDeposit
      },
      {
        id: 'checked',
        renderIcon: () => {
          return (
            <CheckedIconPhoto photoUrl={homechecker?.photoUrl} isVerified />
          );
        },
        renderTitle: () => <TitleChecked firstName={homechecker?.firstName} />,
        renderInfo: () => <CheckedInfo verificationDate={verificationDate} />,
        renderDescription: () => (
          <CheckedDescription
            homechecker={homechecker}
            verificationDate={verificationDate}
            onMoreDetailsClick={openVerificationModal}
            showButtonModal
          />
        ),
        enabled: isChecked
      },
      {
        id: 'noDeposit',
        renderIcon: () => <PiggyBankLabelCircledIcon />,
        renderTitle: () => getTitle(trans('listing.labels.noDeposit.title')),
        renderDescription: () => trans('listing.labels.noDeposit.description'),
        enabled: hasNoDeposit
      },
      {
        id: 'instantBooking',
        renderIcon: () => <InstantBookingLabelCircledIcon />,
        renderTitle: () =>
          getTitle(trans('listing.labels.instantBooking.title')),
        renderDescription: () => (
          <InstantBookingLabelDescription rules={instantBooking?.rules} />
        ),
        enabled: instantBooking?.isEnabled
      },
      {
        id: 'billsIncluded',
        renderIcon: () => <BillsLabelCircledIcon />,
        renderTitle: () => getTitle(trans('listing.labels.bills.all.title')),
        renderDescription: () => trans('listing.labels.bills.description'),
        enabled: allBillsIncluded
      },
      {
        id: 'someBillsIncluded',
        renderIcon: () => <BillsLabelCircledIcon />,
        renderTitle: () => getTitle(trans('listing.labels.bills.some.title')),
        renderDescription: () => trans('listing.labels.bills.description'),
        enabled: someBillsIncluded && !allBillsIncluded
      },
      {
        id: 'bestChoice',
        renderIcon: () => <BestChoiceLabelCircledIcon />,
        renderTitle: () => getTitle(trans('listing.labels.bestChoice.title')),
        renderDescription: () => trans('listing.labels.bestChoice.description'),
        enabled: isBestChoice
      },
      {
        id: 'plus',
        renderIcon: () => <PlusLabelCircledIcon />,
        renderTitle: () => getTitle(trans('Spotahome PLUS')),
        renderDescription: () => <PlusLabelDescription />,
        enabled: isPlusPlan
      },
      {
        id: 'longTerm',
        renderIcon: () => <LongTermLabelCircledIcon />,
        renderTitle: () => getTitle(trans('homecard.label.long-term')),
        renderDescription: () => <LongTermDescription />,
        enabled: allowsPermanentContract
      },
      {
        id: 'llInfo',
        renderIcon: () => (
          <VerificationIconWrapper>
            <HouseLabelCircledIcon />
          </VerificationIconWrapper>
        ),
        renderTitle: () =>
          getTitle(trans('listing.landlord_info.verified_landlord')),
        renderInfo: () => (
          <LLInfo
            landlordInformation={landlordInformation}
            loading={loading}
            onClick={handleOnClick}
          >
            <Button color="link" onClick={openVerificationModal}>
              {trans('listing.verification.more-button')}
            </Button>
          </LLInfo>
        ),
        renderDescription: () => {},
        enabled: !!landlordType
      }
    ];

    return (
      <>
        <VerificationModal
          isOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          checkedIcon={
            isChecked ? (
              <CheckedIconPhoto photoUrl={homechecker?.photoUrl} isVerified />
            ) : null
          }
        />
        <ul className="listing-labels__list">
          {labelsDict.map(
            ({
              renderTitle,
              renderDescription,
              renderIcon,
              renderInfo = () => {},
              enabled,
              id
            }) =>
              enabled && (
                <ListingLabel
                  key={id}
                  icon={renderIcon()}
                  title={renderTitle()}
                  info={renderInfo()}
                >
                  {renderDescription()}
                </ListingLabel>
              )
          )}
        </ul>
      </>
    );
  }
);

ListingLabels.propTypes = {
  isChecked: PropTypes.bool.isRequired,
  hasNoDeposit: PropTypes.bool.isRequired,
  instantBooking: PropTypes.shape(instantBookingShape),
  allBillsIncluded: PropTypes.bool.isRequired,
  someBillsIncluded: PropTypes.bool.isRequired,
  isBestChoice: PropTypes.bool.isRequired,
  isPlusPlan: PropTypes.bool.isRequired,
  allowsPermanentContract: PropTypes.bool.isRequired,
  homechecker: PropTypes.shape(HomeCheckerShape),
  verificationDate: PropTypes.string,
  landlordType: PropTypes.oneOf(['professional', 'private']).isRequired,
  landlordId: PropTypes.string.isRequired
};

ListingLabels.defaultProps = {
  homechecker: null,
  verificationDate: null,
  instantBooking: {}
};

export default ListingLabels;
